import React, { useState } from "react";

export default function UploadData() {
  const [file, setFile] = useState(null);
  const [clientName, setClientName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const N8N_WEBHOOK_URL =
    "https://n8n.srv1626694.hstgr.cloud/webhook/9d24574a-808e-4a09-8a22-d9315bf1b839";

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];

    if (!selected) {
      setFile(null);
      return;
    }

    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "application/csv",
      "text/comma-separated-values",
    ];

    const allowedExtensions = [".xls", ".xlsx", ".csv"];
    const fileName = selected.name.toLowerCase();

    const isValidType =
      allowedTypes.includes(selected.type) ||
      allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValidType) {
      setMessage("Please upload an Excel or CSV file only.");
      setFile(null);
      e.target.value = "";
      return;
    }

    setFile(selected);
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientName.trim()) {
      setMessage("store name is required.");
      return;
    }

    if (!file) {
      setMessage("Please select a file to upload.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const formData = new FormData();
      formData.append("clientName", clientName);
      formData.append("file", file);

      const response = await fetch(N8N_WEBHOOK_URL, {
        method: "POST",
        body: formData,
      });

      const contentType = response.headers.get("content-type") || "";
      let data = {};

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data.message = await response.text();
      }

      if (!response.ok) {
        throw new Error(data.message || "File upload failed.");
      }

      setMessage(data.message || "Your file has been uploaded successfully.");
      setClientName("");
      setFile(null);

      const fileInput = document.getElementById("fileInput");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      setMessage(error.message || "An unexpected error occurred during upload.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Teqa Connect</h1>
            <p className="text-sm text-slate-500 dark:text-slate-500">Upload Data</p>
          </div>

          <div className="rounded-full bg-slate-900 dark:bg-slate-700 px-4 py-2 text-sm font-medium text-white">
            Secure Portal
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="flex flex-col justify-center">
            <span className="mb-3 inline-block w-fit rounded-full bg-slate-200 dark:bg-slate-800 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-300">
              Teqa Connect
            </span>

            <h2 className="text-4xl font-bold leading-tight text-slate-900 dark:text-slate-100">
              Upload your Excel or CSV data securely
            </h2>

            <p className="mt-4 max-w-xl text-base leading-7 text-slate-600 dark:text-slate-400">
              Welcome to Teqa Connect. This secure upload portal allows clients
              to submit data files quickly and easily. Supported formats include
              Excel and CSV files only.
            </p>

            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 rounded-xl bg-white dark:bg-slate-900 p-4 shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 dark:bg-slate-700 text-white">
                  1
                </div>
                <p className="text-slate-700 dark:text-slate-300">Enter your store name</p>
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-white dark:bg-slate-900 p-4 shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 dark:bg-slate-700 text-white">
                  2
                </div>
                <p className="text-slate-700 dark:text-slate-300">Select an Excel or CSV file</p>
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-white dark:bg-slate-900 p-4 shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 dark:bg-slate-700 text-white">
                  3
                </div>
                <p className="text-slate-700 dark:text-slate-300">Click “Upload Data” to submit</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-xl">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Upload Data</h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-500">
                Accepted formats: .xlsx, .xls, .csv
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  store Name
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter store name"
                  className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-4 py-3 outline-none transition focus:border-slate-500 dark:focus:border-slate-500 focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-700"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Upload File
                </label>

                <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-6 py-10 text-center transition hover:border-slate-500 dark:hover:border-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <div className="mb-3 text-4xl">📊</div>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Click to choose a file
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                    Excel and CSV files only
                  </p>

                  <input
                    id="fileInput"
                    type="file"
                    accept=".xls,.xlsx,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>

                {file && (
                  <div className="mt-4 rounded-2xl border border-green-200 dark:border-emerald-500/30 bg-green-50 dark:bg-emerald-500/10 px-4 py-3 text-sm text-green-700 dark:text-emerald-400">
                    Selected file:{" "}
                    <span className="font-semibold">{file.name}</span>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-slate-900 dark:bg-slate-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-700 dark:hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Uploading..." : "Upload Data"}
              </button>

              {message && (
                <div
                  className={`rounded-2xl px-4 py-3 text-sm font-medium ${
                    message.includes("successfully") ||
                    message.toLowerCase().includes("success")
                      ? "bg-green-100 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400"
                      : "bg-red-100 dark:bg-rose-500/10 text-red-700 dark:text-rose-400"
                  }`}
                >
                  {message}
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}